import { Button as CtvButton } from '@accedo/ctv-button';
import { FunctionalComponent, Ref } from 'preact';
import { stylesheet } from 'astroturf';
import { useEffect } from 'preact/hooks';
import { BaseNav, NavProps, NavState } from '@accedo/spatial-nav';
import { KeyCodes, VirtualKeys } from '@accedo/virtual-key';
import { logger } from '@accedo/logger';
import { nav } from '##/init';
import KeyEventHelper from '##/utils/KeyEventHelper';
import { ControlsDetails } from '##/components/Player/PlayerTypes';
import { useMessenger } from '##/utils/hooks';
import { PageNavigation } from '##/utils/PageNavigation';
import { VODCounter, focusCache } from '##/services/store/store';
import { Messengers } from '##/components/Player/utils/Messengers';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { handleVideoClick } from '##/utils/TileClickHandlers';
import { sendApiRequest } from '##/services/tenApi/apirequest';
import { StandardCarouselItem } from '##/components/StandardCarousel/types';
import { LiveCarouselItem } from '##/components/LiveCarousel/types';

const styles = stylesheet`
  @import '##/styles/import.scss';

  .modal-outer-tag {
    position: absolute;
    width: 100%;
    height: 100%;
    background: $pure-black;
    z-index: 99;
    top: 0;
   
  }

  .modal-content-box {
    width: pxToVw(510);
    margin: pxToVh(260) auto 0;
    text-align: center;
    font-family: barlow;
    font-weight: 300;
    color: $off-white;

    
    .modal-header {
      font-size: 2.4rem;
      padding-bottom: 1.5vh;
    }

    .buttons-container {
      display: flex;
    }
  }


  :global(.button).continue-button {
    font-family: barlowsemicondensed;
    font-weight: 500;
    display: block;
    width: pxToVw(244);
    padding: pxToVw(10) pxToVw(27) pxToVw(14);
    margin: pxToVh(17) auto 0;
    border-radius: pxToVw(80);
    font-size: 1.7rem;
    background: rgb(0, 0, 6) 0% 0% no-repeat padding-box;
    border: 1px solid $primary-grey;
    &:global(.current-focus) {
      background: linear-gradient(
          90deg,
          $ten-play-blue 0%,
          rgb(14, 163, 253) 100%
        )
        0% 0% no-repeat padding-box;
      border-color: transparent;
    }
  }
`;

interface InactivePageProps {
  // MOST OF THE PROPS HERE ARE FOR THE LIVE PAGE
  // WHERE THIS COMPONENT WILL BE USED AS A MODAL
  // INSTEAD OF A PAGE
  navRef: Ref<BaseNav<NavProps, NavState >>;
  defaultFocusId: string;
  videoInfo: ControlsDetails;
}

export const InactivePageId = 'unattended-modal-id';

export const InactivePage: FunctionalComponent<InactivePageProps> = () => {
  const routeState = PageNavigation.getCurrentState();
  const liveItems = routeState as LiveCarouselItem;
  const VODitems = routeState as ControlsDetails;
  const stayButtonId = 'start-over-button';
  const showingControls = useMessenger(Messengers.controlsVisible$);
  const isLive = useMessenger(Messengers.isLive$);

  const onPageRouteButtonClick = async (): Promise<void> => {
    if (VODitems.upNextContentApiEndpoint) {
      try {
        const upNextResponse = await sendApiRequest<StandardCarouselItem[]>(
          VODitems?.upNextContentApiEndpoint,
        );
        if (upNextResponse) {
          const nextEpisode = upNextResponse?.shift();
          if (nextEpisode) {
            handleVideoClick(nextEpisode, true);
          }
        }
      } catch (err) {
        logger.error('Error fetching next episode data', err);
        PageNavigation.navigateTo('/');
      }
    }
  };

  const onLiveButtonClick = (): void => {
    if (liveItems.streamUrl) {
      handleVideoClick(liveItems, true);
    }
  };
  const onKeyDown = (keyName: VirtualKeys): boolean => {
    if (
      (keyName === KeyCodes.vKey.LEFT || keyName === KeyCodes.vKey.RIGHT) &&
      nav.currentFocusNodeId === stayButtonId
    ) {
      return true;
    }
    if (keyName === KeyCodes.vKey.BACK) {
      if (!liveItems) {
        if (VODitems?.showId && VODitems?.showApiEndpoint) {
          PageNavigation.replaceRoute(
            `/show/${VODitems.showId}`,
            VODitems.showApiEndpoint,
          );
        } else {
          focusCache.home.$clear();
          PageNavigation.navigateTo('/');
        }
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    VODCounter.$set({ episodeCount: 0 });
    const navigationTimer = setTimeout(() => {
      if (!isLive) {
        if (VODitems?.showId && VODitems?.showApiEndpoint) {
          PageNavigation.navigateTo(
            `/show/${VODitems.id}`,
            VODitems.showApiEndpoint,
          );
        } else {
          focusCache.home.$clear();
          PageNavigation.navigateTo('/');
        }
      } else {
        PageNavigation.handleGlobalBackKey();
      }
    }, 60 * 1000);
    if (routeState) {
      setLoadingIndicatorVisibility?.(false);
    }
    nav.assignFocus(stayButtonId);
    KeyEventHelper.addKeydownListener(onKeyDown, 'unattended-modal-listener');
    return () => {
      KeyEventHelper.removeKeydownListener('unattended-modal-listener');
      clearTimeout(navigationTimer);
    };
  }, []);

  useEffect(() => {
    // NOTE: THIS IS FOR THE LIVE PLAYER
    nav.assignFocus(stayButtonId);
  }, [showingControls]);

  return (
    <div className={styles.modalOuterTag}>
      <div className={styles.modalContentBox}>
        <div className={styles.modalHeader}>Are you still watching?</div>
        <div className={styles.buttonsContainer}>
          <CtvButton
            className={styles.continueButton}
            navId={stayButtonId}
            onClick={isLive ? onLiveButtonClick : onPageRouteButtonClick}
          >
            Continue
          </CtvButton>
        </div>
      </div>
    </div>
  );
};
